import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, Platform, KeyboardAvoidingView, Pressable, ActivityIndicator, ScrollView,
  Modal,
} from 'react-native';
import { Auth } from '@aws-amplify/auth';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Hub } from '@aws-amplify/core';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Animatable from 'react-native-animatable';
import { useDispatch } from 'react-redux';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { addNetworkStateListener } from 'expo-network';
import Colors from '../constants/Colors';
import Style from '../constants/Style';
import getClient from '../requests/SignIn/GetClient';
import config from '../aws-exports';
import OfficeIcon from '../components/OfficeIcon';
import LoginImage from '../components/LoginImage';
import WarningIcon from '../components/60pxIcons/WarningIcon';
import { logEvent, setClientId } from '../components/LogEvent';
import isMobile from '../components/IsMobile';
import { updateDevState } from '../redux/devStateSlice';
import PressableStyled from '../components/PressableStyled';
import CustomTextField from '../components/CustomTextField';
import {
  CLIENT_DOESNT_EXIST, INCORRECT_PASSWORD, OOPS_SOMETHING_WENT_WRONG, PASSWORDS_DONT_MATCH,
  USER_DOESNT_EXIST, USER_NOT_FOUND,
} from '../constants/AlertMessages';
import PasswordHints from '../components/PasswordHints';
import AlertModal from '../components/AlertModal';
import { emailRegex } from '../components/Regex';

// eslint-disable-next-line no-unused-vars
export default function SignInScreen({ navigation, setStatus }) {
  const [text, setText] = useState<string>('');
  const [isErr, updateIsErr] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [userNotFound, setUserNotFound] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [unauth, setUnauth] = useState<boolean>(false);
  const [haveInternet, setHaveInternet] = useState<boolean>(true);
  const [withCredentials, setWithCredentials] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [verificationCodeError, setVerificationCodeError] = useState<string | null>('');
  const [newPasswordError, setNewPasswordError] = useState<string | null>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [changePassword, setChangePassword] = useState<boolean>(false);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [signingIn, setSigningIn] = useState<boolean>(false);
  const [forgotPasswordFlow, setForgotPasswordFlow] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [useDevApi, setUseDevApi] = useState<boolean>(false);

  const showOrganisation: boolean = true;

  const loginButtonDisabled: boolean = !email || loading
    || (forgotPasswordFlow ? !(newPassword && confirmNewPassword && code) : !password);

  useEffect(() => {
    AsyncStorage.getItem('clientName').then((name: string) => {
      if (!text) setText(name);
    });
  }, []);

  useEffect(() => {
    Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
          setSigningIn(true);
          setUnauth(false);
          break;
        case 'cognitoHostedUI':
        case 'signOut':
          setUnauth(false);
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
        default:
          break;
      }
    });
  }, []);

  useEffect(() => {
    const subscription = addNetworkStateListener(({ isInternetReachable }) => {
      setHaveInternet(isInternetReachable);
    });
    return () => {
      subscription.remove();
    };
  }, []);

  const dispatch = useDispatch();

  const onSubmit = async () => {
    const client = await getClient(text?.toLocaleLowerCase(), useDevApi);
    updateIsErr(!client || client?.isError);
    setErrorText(client?.errorMessage === 'client not found' ? CLIENT_DOESNT_EXIST
      : client?.errorMessage ?? CLIENT_DOESNT_EXIST);
    if (client && !client.isError) {
      AsyncStorage.setItem('clientId', client?.clientDetails.app_client_id ?? '');
      AsyncStorage.setItem('poolId', client?.clientDetails.cognito_pool_id ?? '');
      AsyncStorage.setItem('domain', client?.clientDetails.cognito_domain ?? '');
      AsyncStorage.setItem('environment', client?.clientDetails.production_status ?? '');
      dispatch(updateDevState(['dev', 'demo'].includes(client?.clientDetails.production_status)));
      AsyncStorage.setItem('apiDomain', client?.clientDetails.api_domain ?? 'api.hereworks.tech');
      AsyncStorage.setItem('clientName', client?.clientDetails.clientId);
      AsyncStorage.setItem('clientLogo', client?.clientDetails.image_url ?? '');
      setClientId(text.toLocaleLowerCase());
      const updatedConfig = {
        ...config,
        aws_user_pools_id: client?.clientDetails.cognito_pool_id,
        aws_user_pools_web_client_id: client?.clientDetails.app_client_id,
      };
      updatedConfig.oauth.domain = client?.clientDetails.cognito_domain;
      Auth.configure(updatedConfig);
      return client;
    }
    return null;
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  const renderMainView = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'position' : 'height'} style={{ flex: 1, alignItems: 'center' }}>
      <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps='handled'
        >
      <View style={styles.ImageandLoginText}>
        <LoginImage />
        <Text style={styles.title}>{'Welcome to Hereworks Insights'}</Text>
        <View
          style={{
            width: 47,
            height: 2,
            backgroundColor: Colors.light.hereworksPrimaryGrey,
            marginTop: 10,
            marginBottom: 7,
          }}
        />
        <Text style={styles.description}>{withCredentials ? 'Log in with your email and password'
          : 'The Human Centric Approach to Smart Workplaces'}</Text>
        {showOrganisation && (
          <>
          {useDevApi ? <Text style={[styles.description,
            { color: Colors.light.hereworksUrgentRed }]}>Using dev API</Text> : null}
            <CustomTextField
              placeholder='Client ID'
              value={(text ?? '').toLocaleLowerCase()}
              updateValue={(currentText: string) => {
                updateIsErr(false);
                if (currentText === '/dev') {
                  setUseDevApi(true);
                  setText('');
                  return;
                }
                if (currentText === '/prod') {
                  setUseDevApi(false);
                  setText('');
                  return;
                }
                setText(currentText.toLocaleLowerCase());
              }}
              invalid={isErr || userNotFound}
              errorText={isErr ? errorText : ''}
              disableAuto
            />
            {unauth && (
              <Text style={styles.errorText}>
                {'Not authorized, please contact admin.'}
              </Text>
            )}
          </>
        )}
      </View>
      {withCredentials ? null
        : <View style={[{ alignItems: 'center' }, showOrganisation ? {} : { marginTop: 56 }]}>
          <PressableStyled
            onPress={() => {
              logEvent('SignIn', 'log_in_sso');
              onSubmit().then((client) => {
                if (client && !client?.isErr) {
                  AsyncStorage.setItem('changePasswordEnabled', 'false');
                  Auth.federatedSignIn({
                    customProvider:
                      client?.clientDetails.ssoProviderName ?? 'MckeonAzureProvider',
                  });
                }
              });
            }}
            style={styles.button}
          >
            <Text style={styles.buttonText}>{'Login with Office 365'}</Text>
            <OfficeIcon />
          </PressableStyled>
        </View>}
      {withCredentials ? null
        : <View style={{ alignItems: 'center', marginTop: 10 }}>
          <PressableStyled
            onPress={() => {
              logEvent('SignIn', 'log_in_credentials');
              onSubmit().then((client) => {
                if (client && !client?.isErr) setWithCredentials(true);
              });
            }}
            style={styles.secondaryButton}
          >
            <Text style={styles.secondaryButtonText}>{'Login with Credentials'}</Text>
          </PressableStyled>
        </View>}
      {withCredentials ? <View style={{ alignItems: 'center', marginTop: 10 }}>
        <CustomTextField
          placeholder='Email'
          value={(email ?? '').toLocaleLowerCase()}
          updateValue={(currentText: string) => {
            updateIsErr(false);
            setUserNotFound(false);
            setEmail(currentText.toLocaleLowerCase());
          }}
          errorText={userNotFound ? USER_NOT_FOUND : emailError}
          invalid={(isErr && !email) || userNotFound || !!emailError}
          disableAuto
        />
        {(changePassword || forgotPasswordFlow) ? null : <View
          style={{ alignItems: 'center', marginTop: 10, alignSelf: 'stretch' }}>
          <CustomTextField
            placeholder={'Password'}
            value={(password ?? '')}
            updateValue={(currentText: string) => {
              updateIsErr(false);
              setPassword(currentText);
            }}
            invalid={(isErr && !password) || !!passwordError}
            errorText={passwordError}
            password
            disableAuto
          />
        </View>}
        {(changePassword || forgotPasswordFlow) ? null : <PressableStyled
          disabled={!email}
          style={{ opacity: email ? 1 : 0.3 }}
          onPress={() => Auth.forgotPassword(email?.trim()).then(() => {
            setForgotPasswordFlow(true);
            showAlert(
              'Recovery code sent',
              'Your password reset code has been sent. Please check your email',
            );
            setPassword('');
          }).catch((value) => {
            if (value?.message?.toLowerCase().includes('combination not found')) {
              setUserNotFound(true);
              return;
            }
            updateIsErr(true);
            setErrorText(value?.message ?? OOPS_SOMETHING_WENT_WRONG);
          })}>
          <Text style={styles.secondaryButtonText}>Forgot your password?</Text>
        </PressableStyled>}
        {forgotPasswordFlow ? <><CustomTextField
          title='Reset Password Code'
          placeholder='Code'
          value={(code ?? '')}
          updateValue={(currentText: string) => {
            setVerificationCodeError(null);
            setCode(currentText);
          }}
          invalid={(isErr && !code) || !!verificationCodeError}
          errorText={verificationCodeError}
          disableAuto
        />
        </> : null}
        {(changePassword || forgotPasswordFlow) ? <View style={styles.fieldContainer}>
          <CustomTextField
            title='New Password'
            placeholder='Password'
            value={(newPassword ?? '')}
            updateValue={(currentText: string) => {
              updateIsErr(false);
              setNewPassword(currentText);
            }}
            invalid={!!newPasswordError}
            errorText={newPasswordError}
            password
            disableAuto
          />
        </View> : null}
        {(changePassword || forgotPasswordFlow) ? <View style={styles.fieldContainer}>
          <CustomTextField
            title='Confirm New Password'
            placeholder='Password'
            value={(confirmNewPassword ?? '')}
            updateValue={(currentText: string) => {
              updateIsErr(false);
              setConfirmNewPassword(currentText);
            }}
            invalid={isErr && !confirmNewPassword}
            password
            disableAuto
          />
          <PasswordHints />
        </View> : null}
        <PressableStyled
          onPress={() => {
            logEvent('SignIn', 'log_in_btn');
            setUserNotFound(false);
            if (!emailRegex.exp.test(email.trim())) {
              setEmailError(emailRegex.message);
              return;
            }
            if (!forgotPasswordFlow && !(password.trim().length)) {
              setPasswordError('Please enter your password');
              return;
            }
            setLoading(true);
            setUnauth(false);
            setEmailError('');
            onSubmit().then((client) => {
              setPasswordError('');
              setVerificationCodeError(null);
              setNewPasswordError(null);
              setUserNotFound(false);
              if (client && !client?.isErr) {
                // for local dev:
                // Auth.signIn('user@hereworks.ie', 'password');
                if (forgotPasswordFlow) {
                  if (newPassword !== confirmNewPassword) {
                    setNewPasswordError(PASSWORDS_DONT_MATCH);
                  } else {
                    Auth.forgotPasswordSubmit(email?.trim(), code, newPassword).then(() => {
                      Auth.signIn(email?.trim(), newPassword);
                    }).catch((val) => {
                      updateIsErr(false);
                      if (val?.message?.toLowerCase().includes('invalid verification code')) {
                        setVerificationCodeError(val?.message);
                      } else if (val?.message?.toLowerCase().includes('not found')) {
                        setUserNotFound(true);
                      } else setNewPasswordError(val?.message ?? OOPS_SOMETHING_WENT_WRONG);
                    });
                  }
                } else if (changePassword) {
                  if (newPassword !== confirmNewPassword) {
                    updateIsErr(true);
                    setNewPasswordError(PASSWORDS_DONT_MATCH);
                  } else {
                    user.completeNewPasswordChallenge(newPassword, [], {
                      onSuccess: () => {
                        Auth.signIn(email?.trim(), newPassword);
                      },
                      onFailure: (err) => {
                        updateIsErr(true);
                        if (err?.name === 'InvalidPasswordException') {
                          setErrorText('* Password must contain a lower case letter\n* Password must contain an upper case letter\n* Password must contain a number\n* Password must contain at least 8 characters\n* Password must contain a special character or a space\n* Password must not contain a leading or trailing space');
                        } else setErrorText(err?.name ?? JSON.stringify(err));
                      },
                    });
                  }
                } else {
                  AsyncStorage.setItem('changePasswordEnabled', 'true');
                  Auth.signIn(email?.trim(), password).then((val) => {
                    if (val?.challengeName) {
                      setUser(val);
                      setChangePassword(true);
                    }
                  })
                    .catch((ex) => {
                      const output: string | undefined = ex?.message?.toLowerCase();
                      if (output?.includes(USER_DOESNT_EXIST.toLowerCase())
                        || output.includes('not enabled')
                        || output?.includes('combination not found')) {
                        setUserNotFound(true);
                      } else if (output?.includes('incorrect username or password.')) {
                        setPasswordError(INCORRECT_PASSWORD);
                      } else setPasswordError(output);
                    })
                    .finally(() => setLoading(false));
                }
              }
            }).finally(() => setLoading(false));
          }}
          style={[styles.button, { opacity: loginButtonDisabled ? 0.3 : 1 }]}
          disabled={loginButtonDisabled}
          testID='login-btn'
        >
          <Text style={styles.buttonText}>{'Log In'}</Text>
          {loading ? <ActivityIndicator
            color={'#000'}
            style={{ position: 'absolute' }}
          /> : null}
        </PressableStyled>
        <PressableStyled
          onPress={() => {
            logEvent('SignIn', 'back');
            setEmail('');
            setErrorText('');
            setPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setWithCredentials(false);
            setChangePassword(false);
            setUser(null);
            setUnauth(false);
            setForgotPasswordFlow(false);
          }}
          style={styles.secondaryButton}
        >
          <Text style={styles.secondaryButtonText}>{'Back'}</Text>
        </PressableStyled>
      </View> : null}
      {isMobile ? <View style={{ alignItems: 'center' }}>
        {helpButton()}
      </View> : null}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  const helpButton = () => (
    <Pressable style={(state) => [{
      backgroundColor: (state.hovered || state.pressed) ? Colors.light.hereworksBlue200 : undefined,
      padding: 8,
      marginTop: 8,
      borderRadius: 8,
    }]}
      onPress={() => {
        logEvent('SignIn', 'need_help');
        navigation.navigate('Support');
      }}>
      <Text style={styles.helpText}>{'Need help?'}</Text>
    </Pressable>
  );

  if (isMobile) {
    return (
      signingIn ? <Modal>
        <ActivityIndicator
          style={styles.loadingIndicator}
          color={Colors.light.hereworksBlue500}
          size={'large'}
        />
      </Modal>
        : <View style={{ flex: 1, backgroundColor: '#fff' }}>
          {renderMainView()}
          {!haveInternet && <Animatable.View
            animation={'slideInDown'}
            style={styles.offlineContainer}
          >
            <WarningIcon color={Colors.light.hereworksBlue500} />
            <Text style={styles.offlineText}>{'You appear to be offline'}</Text>
          </Animatable.View>}
          <AlertModal
            modalVisible={alertVisible}
            setModalVisible={setAlertVisible}
            title={alertTitle}
            message={alertMessage}
            isMessage
          />
        </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.innerContainer, Style.boxShadow]}>
        {renderMainView()}
      </View>
      {helpButton()}
      <AlertModal
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        title={alertTitle}
        message={alertMessage}
        isMessage
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  innerContainer: {
    justifyContent: 'center',
    alignSelf: 'center',
    borderRadius: 24,
    padding: 40,
    backgroundColor: 'white',
  },
  ImageandLoginText: {
    alignItems: 'center',
    marginTop: isMobile ? 16 : 0,
  },
  title: {
    fontSize: 24,
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    marginHorizontal: 40,
    marginTop: 24,
  },
  description: {
    fontFamily: 'Poppins_400Regular',
    alignItems: 'center',
    fontSize: 18,
    color: Colors.light.hereworksPrimaryGrey800,
    textAlign: 'center',
    marginBottom: 24,
  },
  errorText: {
    fontFamily: 'Poppins_400Regular',
    alignItems: 'center',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 15,
  },
  button: {
    backgroundColor: Colors.light.hereworksBlue500,
    width: '100%',
    padding: 10,
    marginTop: 20,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButton: {
    backgroundColor: '#fff',
    borderWidth: 2,
    borderColor: Colors.light.hereworksLightBlue,
    width: '100%',
    paddingVertical: 7,
    marginTop: 16,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: 'white',
    textAlign: 'center',
    paddingVertical: 7,
    marginHorizontal: 8,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
  },
  secondaryButtonText: {
    color: Colors.light.hereworksLightBlue,
    textAlign: 'center',
    paddingVertical: 7,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
  },
  fieldContainer: {
    alignItems: 'center',
    marginTop: 10,
    alignSelf: 'stretch',
  },
  loadingIndicator: {
    backgroundColor: '#fff',
    height: '100%',
  },
  scrollView: {
    alignContent: 'center',
    backgroundColor: '#fff',
    borderRadius: 24,
    paddingHorizontal: 8,
    flex: 1,
  },
  scrollViewContent: {
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 40,
    flexGrow: 1,
  },
  offlineContainer: {
    backgroundColor: '#fff',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: 54,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  offlineText: {
    color: Colors.light.hereworksBlue500,
    fontFamily: 'Poppins_500Medium',
    marginTop: 14,
  },
  helpText: {
    color: Colors.light.hereworksBlue500,
    fontSize: 12,
    fontFamily: 'Roboto_400Regular',
  },
});
